import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, CircularProgress, Alert, Stack, Chip, Divider,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

/**
 * Public, unauthenticated share page for a Soundcheck track.
 * Route: /soundcheck/share/:uuid
 *
 * Anyone with the link can listen and download the MP3 as an actual file.
 * The MP3 is streamed through the app so the URL stays on command.audiocommand.com
 * and the download comes with Content-Disposition: attachment.
 */
export default function SoundcheckShare() {
  const { uuid } = useParams();
  const [track, setTrack] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/soundcheck/public/${uuid}`, { credentials: 'omit' })
      .then(async (r) => {
        if (!r.ok) {
          throw new Error(r.status === 404
            ? 'This Soundcheck link is no longer active.'
            : 'Could not load this Soundcheck.');
        }
        return r.json();
      })
      .then((data) => { if (!cancelled) setTrack(data); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [uuid]);

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#f5f7fa',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      py: { xs: 4, sm: 8 },
      px: 2,
    }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>
          Audio Command Systems
        </Typography>
        <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>
          Soundcheck
        </Typography>
      </Box>

      <Paper elevation={0} sx={{
        p: { xs: 3, sm: 5 },
        border: '1px solid #e8edf2',
        borderRadius: 2,
        maxWidth: 640,
        width: '100%',
        bgcolor: '#fff',
      }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="warning">{error}</Alert>
        )}

        {!loading && track && (
          <>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#0D2C54', mb: 0.5 }}>
              {track.title}
            </Typography>
            {(track.artists && track.artists.length > 0) && (
              <Typography variant="body2" sx={{ color: '#5a6b7a', mb: 1 }}>
                {(track.artists || []).join(' × ')}
              </Typography>
            )}
            <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 0.5 }}>
              {track.duration_seconds ? (
                <Chip size="small" label={`${track.duration_seconds}s`} />
              ) : null}
              {track.owner_name ? (
                <Chip size="small" variant="outlined" label={`Shared by ${track.owner_name}`} />
              ) : null}
            </Stack>

            <audio
              controls
              src={track.stream_url}
              style={{ width: '100%', marginBottom: 24 }}
            >
              Your browser doesn't support audio playback.
            </audio>

            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              startIcon={<DownloadIcon />}
              component="a"
              href={track.download_url}
            >
              Download MP3
            </Button>

            {track.vibe && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#8898A4', letterSpacing: 1 }}>VIBE</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, mb: 2 }}>{track.vibe}</Typography>
              </>
            )}
            {track.about && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#8898A4', letterSpacing: 1 }}>ABOUT</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, mb: 2 }}>{track.about}</Typography>
              </>
            )}
            {track.lyrics && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#8898A4', letterSpacing: 1 }}>LYRICS</Typography>
                <Typography variant="body2" component="pre" sx={{
                  fontFamily: 'inherit',
                  whiteSpace: 'pre-wrap',
                  mt: 0.5,
                }}>
                  {track.lyrics}
                </Typography>
              </>
            )}
          </>
        )}
      </Paper>

      <Typography variant="caption" sx={{ color: '#8898A4', mt: 4 }}>
        © {new Date().getFullYear()} Audio Command Systems
      </Typography>
    </Box>
  );
}
